import React, { useState, useEffect } from 'react';
import './App.css';
import { 
  ChartBarIcon, 
  ArrowPathIcon, 
  ClockIcon, 
  BoltIcon,
  CurrencyDollarIcon,
  RocketLaunchIcon,
  CpuChipIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

// Direct image paths - optimized WebP images
const imageUrls = {
  automation1: '/process/ai/aiinf1.webp',
  automation2: '/process/ai/fourthprcesimg.webp',
  automation3: '/process/ai/aiinf3.webp',
  automation4: '/process/ai/fifthimgprocess.webp',
  demo1: '/demo/1.webp',
  demo2: '/demo/2.webp',
  demo3: '/demo/3.webp'
};

// Language content object
const content = {
  en: {
    hero: {
      title1: "We Don't Sell AI.",
      title2: "We Sell Output.",
      subtitle: "Every automation is engineered to save time, cut costs, or increase revenue—or it doesn't get built.",
      cta1: "See What We'd Replace",
      cta2: "See How It Works"
    },
    replaceTasks: {
      title: "Stop Wasting Time. Start Automating.",
      subtitle: "See exactly how we eliminate these daily headaches—and what you get instead.",
      tasks: [
        {
          problem: "Customer Support Emails",
          problemDetail: "Answering the same questions 50 times a day",
          timeWasted: "12h/week",
          solution: "AI Support Agent",
          solutionDetail: "Instant responses, 24/7 availability, learns from your docs",
          timeSaved: "11h/week saved",
          benefits: ["Handles 85% of inquiries", "Always online", "Multi-language"]
        },
        {
          problem: "Appointment Scheduling",
          problemDetail: "Back-and-forth emails to find a meeting time",
          timeWasted: "6h/week",
          solution: "Smart Booking Bot",
          solutionDetail: "Auto-syncs calendars, sends reminders, reschedules conflicts",
          timeSaved: "5.5h/week saved",
          benefits: ["Calendar sync", "Auto-reminders", "Zero conflicts"]
        },
        {
          problem: "Data Entry",
          problemDetail: "Copying information between spreadsheets and systems",
          timeWasted: "10h/week",
          solution: "Auto-Sync System",
          solutionDetail: "Real-time data transfer across all your tools",
          timeSaved: "9h/week saved",
          benefits: ["Real-time sync", "Zero errors", "Multi-platform"]
        },
        {
          problem: "Lead Qualification",
          problemDetail: "Manually reviewing and scoring incoming leads",
          timeWasted: "8h/week",
          solution: "Lead Scoring AI",
          solutionDetail: "Instant qualification based on your criteria",
          timeSaved: "7h/week saved",
          benefits: ["Instant scoring", "CRM integration", "Priority alerts"]
        },
        {
          problem: "Invoice Processing",
          problemDetail: "Creating, sending, and tracking invoices manually",
          timeWasted: "5h/week",
          solution: "Invoice Automation",
          solutionDetail: "Auto-generate, send, and track with payment reminders",
          timeSaved: "4.5h/week saved",
          benefits: ["Auto-generation", "Payment tracking", "Reminders"]
        },
        {
          problem: "Social Media Responses",
          problemDetail: "Replying to comments and messages across platforms",
          timeWasted: "7h/week",
          solution: "Social AI Manager",
          solutionDetail: "Responds to comments, DMs, and mentions instantly",
          timeSaved: "6h/week saved",
          benefits: ["Multi-platform", "Brand voice", "Instant replies"]
        }
      ]
    },
    stats: {
      marketing: "AI Infrastructure Results",
      automation: "Automation Results", 
      combined: "Combined Results (AI + Automation)"
    },
    process: {
      title: "Our Process",
      step1: {
        title: "Analysis & Strategy",
        description: "We analyze your current processes and identify automation opportunities"
      },
      step2: {
        title: "AI Implementation", 
        description: "We build custom AI solutions tailored to your specific needs"
      },
      step3: {
        title: "Integration & Testing",
        description: "We integrate the solutions and thoroughly test everything"
      },
      step4: {
        title: "Launch & Optimization",
        description: "We launch and continuously optimize your AI infrastructure"
      }
    },
    results: {
      title: "Results That Speak for Themselves",
      subtitle: "Real results from real clients who chose to act",
      marketing: {
        title: "Marketing Automation",
        leads: "More leads",
        conversion: "Higher conversion", 
        cost: "Lower cost per acquisition"
      },
      automation: {
        title: "Process Automation",
        time: "Time saved daily",
        efficiency: "Increased efficiency",
        errors: "Fewer human errors"
      },
      combined: {
        title: "Combined Impact", 
        revenue: "Revenue increase",
        roi: "ROI in first 6 months",
        growth: "Sustainable growth"
      }
    },
    objections: {
      title: "We Understand Your Concerns",
      subtitle: "Our clients had the same doubts before seeing the results",
      list: {
        1: "\"It's too expensive\"",
        2: "\"I don't have that budget\"", 
        3: "\"What guarantee do I have?\"",
        4: "\"I've tried before and it didn't work\"",
        5: "\"We want to analyze more options\"",
        6: "\"I need to think about it\""
      },
      responses: {
        1: {
          title: "Understand the real value of your investment",
          description: "A client earns on average 7x more than they invest in the first 6 months."
        },
        2: {
          title: "Flexible payment options and guaranteed ROI",
          description: "Pay in installments and guarantee to recover your investment in 90 days."
        },
        3: {
          title: "Our results guarantee and full support",
          description: "Money-back guarantee if you don't see results in the first 90 days."
        },
        4: {
          title: "Why our approach is different and effective",
          description: "Our methods are tested and customized for each individual business."
        },
        5: {
          title: "Comparison with other market solutions",
          description: "Free analysis versus other agencies and why we are the right choice."
        },
        6: {
          title: "How to convince your team of the value of this decision",
          description: "Materials and case studies to present to your team."
        }
      },
      inProduction: "🚧 In Production 🚧",
      comingSoon: "This video is being created",
      available: "Available soon",
      videoTitles: {
        approach: "Our different approach",
        comparison: "Market comparison",
        team: "Team convincing"
      }
    },
    infrastructure: {
      title: "Our AI Infrastructure",
      description: "From simple things like WhatsApp receptionists to complex systems like automated advertising poster generation. You can add multiple AI agents in one system, giving you a complete team of agents without paying tens of thousands of dollars per month for salaries. Most importantly - it works 24/7, no matter where you are."
    },
    demos: {
      appointment: {
        title: "Automated Booking System",
        description: "Fully automated chatbot for WhatsApp, Telegram and any chat platform. Creates, edits and automatically deletes events in Google Calendar, and sends confirmations via email.",
        features: {
          calendar: "Complete Google Calendar integration",
          email: "Automatic email confirmations",
          multiplatform: "Multi-platform support (WhatsApp, Telegram, etc.)",
          availability: "Complete availability management"
        }
      },
      leadScraper: {
        title: "Google Maps Lead Scraper with AI",
        description: "Automatically extracts leads from Google Maps, analyzes with AI to ensure they are qualified and from the right niche, then provides you with filtered results ready for contact.",
        features: {
          extraction: "Automated extraction from Google Maps",
          analysis: "AI analysis for lead qualification",
          filtering: "Filtering by niche and specific criteria",
          export: "Organized export with complete contact data"
        }
      }
    },
    contact: {
      title: "Let's automate your business",
      subtitle: "Complete the form and we'll email you within 48h",
      fields: {
        name: "Full Name",
        email: "Email Address",
        businessName: "Business Name",
        business: "Business Type (e.g., Restaurant, E-commerce)"
      },
      button: "Send Request",
      note: "We'll email you within 48 hours"
    }
  },
  ja: {
    hero: {
      title1: "AIを売るのではなく、",
      title2: "成果を売ります。",
      subtitle: "すべての自動化は、時間の節約、コスト削減、または収益の増加を目的としています。そうでなければ、構築しません。",
      cta1: "何を置き換えるか見る",
      cta2: "仕組みを見る"
    },
    replaceTasks: {
      title: "時間の無駄を止めて自動化を開始",
      subtitle: "これらの日常の頭痛を正確にどのように解消し、代わりに何を得るかをご覧ください。",
      tasks: [
        {
          problem: "カスタマーサポートメール",
          problemDetail: "同じ質問に1日50回答える",
          timeWasted: "週12時間",
          solution: "AIサポートエージェント",
          solutionDetail: "即座の応答、24時間365日対応、ドキュメントから学習",
          timeSaved: "週11時間節約",
          benefits: ["85%の問い合わせに対応", "常時オンライン", "多言語対応"]
        },
        {
          problem: "アポイント調整",
          problemDetail: "会議時間を見つけるための往復メール",
          timeWasted: "週6時間",
          solution: "スマート予約ボット",
          solutionDetail: "カレンダー自動同期、リマインダー送信、競合の再調整",
          timeSaved: "週5.5時間節約",
          benefits: ["カレンダー同期", "自動リマインダー", "競合ゼロ"]
        },
        {
          problem: "データ入力",
          problemDetail: "スプレッドシートとシステム間で情報をコピー",
          timeWasted: "週10時間",
          solution: "自動同期システム",
          solutionDetail: "すべてのツール間でリアルタイムデータ転送",
          timeSaved: "週9時間節約",
          benefits: ["リアルタイム同期", "エラーゼロ", "マルチプラットフォーム"]
        },
        {
          problem: "リードの選別",
          problemDetail: "受信したリードを手動で確認・評価",
          timeWasted: "週8時間",
          solution: "リードスコアリングAI",
          solutionDetail: "基準に基づいて即座に評価",
          timeSaved: "週7時間節約",
          benefits: ["即座のスコアリング", "CRM統合", "優先アラート"]
        },
        {
          problem: "請求書処理",
          problemDetail: "請求書の作成、送信、追跡を手動で実行",
          timeWasted: "週5時間",
          solution: "請求書自動化",
          solutionDetail: "支払いリマインダー付きで自動生成、送信、追跡",
          timeSaved: "週4.5時間節約",
          benefits: ["自動生成", "支払い追跡", "リマインダー"]
        },
        {
          problem: "ソーシャルメディア対応",
          problemDetail: "複数のプラットフォームでコメントやメッセージに返信",
          timeWasted: "週7時間",
          solution: "ソーシャルAIマネージャー",
          solutionDetail: "コメント、DM、メンションに即座に応答",
          timeSaved: "週6時間節約",
          benefits: ["マルチプラットフォーム", "ブランドボイス", "即座の返信"]
        }
      ]
    },
    stats: {
      marketing: "AIインフラ結果",
      automation: "自動化結果",
      combined: "統合結果（AI + 自動化）"
    },
    process: {
      title: "私たちのプロセス",
      step1: {
        title: "分析・戦略",
        description: "現在のプロセスを分析し、自動化の機会を特定します"
      },
      step2: {
        title: "AI実装",
        description: "お客様の特定のニーズに合わせたカスタムAIソリューションを構築します"
      },
      step3: {
        title: "統合・テスト",
        description: "ソリューションを統合し、すべてを徹底的にテストします"
      },
      step4: {
        title: "ローンチ・最適化",
        description: "ローンチし、AIインフラストラクチャを継続的に最適化します"
      }
    },
    results: {
      title: "結果が物語る",
      subtitle: "行動を選択した実際のクライアントからの実際の結果",
      marketing: {
        title: "マーケティング自動化",
        leads: "より多くのリード",
        conversion: "より高いコンバージョン",
        cost: "より低い獲得コスト"
      },
      automation: {
        title: "プロセス自動化",
        time: "毎日節約される時間",
        efficiency: "効率性の向上",
        errors: "人的エラーの減少"
      },
      combined: {
        title: "統合効果",
        revenue: "収益増加",
        roi: "最初の6ヶ月でのROI",
        growth: "持続可能な成長"
      }
    },
    objections: {
      title: "あなたの懸念を理解しています",
      subtitle: "結果を見る前は、私たちのクライアントも同じ疑問を抱いていました",
      list: {
        1: "「高すぎる」",
        2: "「そんな予算はない」",
        3: "「どんな保証があるのか？」",
        4: "「以前試したがうまくいかなかった」",
        5: "「もっと多くの選択肢を分析したい」",
        6: "「考える必要がある」"
      },
      responses: {
        1: {
          title: "投資の真の価値を理解する",
          description: "クライアントは最初の6ヶ月で平均して投資額の7倍を稼ぎます。"
        },
        2: {
          title: "柔軟な支払いオプションと保証されたROI",
          description: "分割払いで、90日以内に投資を回収することを保証します。"
        },
        3: {
          title: "私たちの結果保証と完全サポート",
          description: "最初の90日で結果が見られない場合の返金保証。"
        },
        4: {
          title: "なぜ私たちのアプローチが異なり効果的なのか",
          description: "私たちの方法は、個々のビジネスに合わせてテストされ、カスタマイズされています。"
        },
        5: {
          title: "他の市場ソリューションとの比較",
          description: "他の代理店との無料分析と、なぜ私たちが正しい選択なのか。"
        },
        6: {
          title: "チームにこの決定の価値を納得させる方法",
          description: "チームに提示するための資料とケーススタディ。"
        }
      },
      inProduction: "🚧 制作中 🚧",
      comingSoon: "この動画は作成中です",
      available: "近日利用可能",
      videoTitles: {
        approach: "私たちの異なるアプローチ",
        comparison: "市場比較",
        team: "チームの説得"
      }
    },
    infrastructure: {
      title: "私たちのAIインフラストラクチャ",
      description: "WhatsApp受付係のような簡単なものから、自動広告ポスター生成のような複雑なシステムまで。1つのシステムで複数のAIエージェントを追加でき、給与で月数百万円を支払うことなく、完全なエージェントチームを提供できます。最も重要なことは、どこにいても24時間年中無休で動作することです。"
    },
    demos: {
      appointment: {
        title: "自動予約システム",
        description: "WhatsApp、Telegram、およびあらゆるチャットプラットフォーム向けの完全自動化チャットボット。Googleカレンダーでイベントを作成、編集、自動削除し、メールで確認を送信します。",
        features: {
          calendar: "完全なGoogleカレンダー統合",
          email: "自動メール確認",
          multiplatform: "マルチプラットフォーム対応（WhatsApp、Telegramなど）",
          availability: "完全な予約管理"
        }
      },
      leadScraper: {
        title: "AIを活用したGoogle Mapsリードスクレイパー",
        description: "Google Mapsからリードを自動抽出し、AIで分析して適格性とニッチを確認し、連絡可能なフィルタリング済み結果を提供します。",
        features: {
          extraction: "Google Mapsからの自動抽出",
          analysis: "リード資格のAI分析",
          filtering: "ニッチと特定基準によるフィルタリング",
          export: "完全な連絡先データを含む整理されたエクスポート"
        }
      }
    },
    contact: {
      title: "ビジネスを自動化しましょう",
      subtitle: "フォームに記入していただければ、48時間以内にメールいたします",
      fields: {
        name: "お名前",
        email: "メールアドレス",
        businessName: "ビジネス名",
        business: "事業タイプ（例：レストラン、Eコマース）"
      },
      button: "リクエスト送信",
      note: "48時間以内にメールいたします"
    }
  }
};

const App: React.FC = () => {
    // Detect language based on URL path
    const [language, setLanguage] = useState<'en' | 'ja'>('en'); // Default to English
    const [showVignette, setShowVignette] = useState(true);
    
    // Carousel state for automation cards only
    const [automationCurrentCard, setAutomationCurrentCard] = useState(0);
    
    // Carousel state for infrastructure demos
    const [infrastructureIndex, setInfrastructureIndex] = useState(0);

    // Form state
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        businessName: '',
        business: ''
    });
    const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    
    // Toast notification state
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState<'success' | 'error'>('success');

    // Store animation data for smooth cursor following with fade effects
    const animationData = React.useRef(new Map());

    // Detect language from URL
    useEffect(() => {
        const path = window.location.pathname;
        if (path.includes('/ja') || path.startsWith('/ja')) {
            setLanguage('ja');
        } else {
            setLanguage('en'); // Default to English for all other paths
        }
    }, []);

    // Get current content based on language
    const t = content[language];

    // Carousel navigation functions
    const goToNextCard = () => {
        setAutomationCurrentCard(prev => prev < 3 ? prev + 1 : 0); // 4 cards (0, 1, 2, 3)
    };

    const goToPrevCard = () => {
        setAutomationCurrentCard(prev => prev > 0 ? prev - 1 : 3); // 4 cards (0, 1, 2, 3)
    };

    const goToCard = (index: number) => {
        setAutomationCurrentCard(index);
    };

    // Smooth cursor following animation with fade in/out starting from cursor entry point
    const smoothFollowCursor = (card: HTMLElement, targetX: number, targetY: number, targetOpacity: number = 1, isInitialEntry: boolean = false) => {
        const cardId = card.dataset.cardId || Math.random().toString();
        card.dataset.cardId = cardId;

        let data = animationData.current.get(cardId);
        if (!data || isInitialEntry) {
            // Start from cursor position on initial entry, not center
            data = { 
                currentX: isInitialEntry ? targetX : (data?.currentX ?? 50), 
                currentY: isInitialEntry ? targetY : (data?.currentY ?? 50), 
                currentOpacity: 0, 
                animationFrame: null 
            };
            animationData.current.set(cardId, data);
        }

        // Cancel previous animation
        if (data.animationFrame) {
            cancelAnimationFrame(data.animationFrame);
        }

        const animate = () => {
            // Smooth interpolation for position and opacity
            const lerpFactor = 0.08; // Position lag effect
            const opacityLerpFactor = 0.03; // Much slower fade (approximately 1 second)
            
            data.currentX += (targetX - data.currentX) * lerpFactor;
            data.currentY += (targetY - data.currentY) * lerpFactor;
            data.currentOpacity += (targetOpacity - data.currentOpacity) * opacityLerpFactor;

            card.style.setProperty('--mouse-x', `${data.currentX}%`);
            card.style.setProperty('--mouse-y', `${data.currentY}%`);
            card.style.setProperty('--light-opacity', data.currentOpacity.toString());

            // Continue animation if not close enough to target (position or opacity)
            const positionDistance = Math.sqrt(
                Math.pow(targetX - data.currentX, 2) + Math.pow(targetY - data.currentY, 2)
            );
            const opacityDistance = Math.abs(targetOpacity - data.currentOpacity);

            if ((positionDistance > 0.5 || opacityDistance > 0.01) && (card.matches(':hover') || targetOpacity === 0)) {
                data.animationFrame = requestAnimationFrame(animate);
            }
        };

        data.animationFrame = requestAnimationFrame(animate);
    };

    const handleCardMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
        const card = e.currentTarget;
        const rect = card.getBoundingClientRect();
        const entryX = ((e.clientX - rect.left) / rect.width) * 100;
        const entryY = ((e.clientY - rect.top) / rect.height) * 100;
        
        if (card.classList.contains('card-color-shift')) {
            const calculateColor = (xPercent: number): { r: number, g: number, b: number } => {
                if (xPercent < 35) {
                    return { r: 239, g: 68, b: 68 };
                } else if (xPercent < 50) {
                    const t = (xPercent - 35) / 15;
                    return {
                        r: Math.round(239 + (234 - 239) * t),
                        g: Math.round(68 + (179 - 68) * t),
                        b: Math.round(68 + (8 - 68) * t)
                    };
                } else if (xPercent < 65) {
                    const t = (xPercent - 50) / 15;
                    return {
                        r: Math.round(234 + (34 - 234) * t),
                        g: Math.round(179 + (197 - 179) * t),
                        b: Math.round(8 + (94 - 8) * t)
                    };
                } else {
                    return { r: 34, g: 197, b: 94 };
                }
            };
            
            const color = calculateColor(entryX);
            
            card.style.setProperty('--glow-color-r', color.r.toString());
            card.style.setProperty('--glow-color-g', color.g.toString());
            card.style.setProperty('--glow-color-b', color.b.toString());
            card.style.setProperty('--mouse-x', `${entryX}%`);
            card.style.setProperty('--mouse-y', `${entryY}%`);
        } else {
            smoothFollowCursor(card, entryX, entryY, 1, true);
        }
    };

    const handleCardMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const card = e.currentTarget;
        const rect = card.getBoundingClientRect();
        const targetX = ((e.clientX - rect.left) / rect.width) * 100;
        const targetY = ((e.clientY - rect.top) / rect.height) * 100;
        
        if (card.classList.contains('card-color-shift')) {
            card.style.setProperty('--mouse-x', `${targetX}%`);
            card.style.setProperty('--mouse-y', `${targetY}%`);
            
            const calculateColor = (xPercent: number): { r: number, g: number, b: number } => {
                if (xPercent < 35) {
                    return { r: 239, g: 68, b: 68 };
                } else if (xPercent < 50) {
                    const t = (xPercent - 35) / 15;
                    return {
                        r: Math.round(239 + (234 - 239) * t),
                        g: Math.round(68 + (179 - 68) * t),
                        b: Math.round(68 + (8 - 68) * t)
                    };
                } else if (xPercent < 65) {
                    const t = (xPercent - 50) / 15;
                    return {
                        r: Math.round(234 + (34 - 234) * t),
                        g: Math.round(179 + (197 - 179) * t),
                        b: Math.round(8 + (94 - 8) * t)
                    };
                } else {
                    return { r: 34, g: 197, b: 94 };
                }
            };
            
            const color = calculateColor(targetX);
            card.style.setProperty('--glow-color-r', color.r.toString());
            card.style.setProperty('--glow-color-g', color.g.toString());
            card.style.setProperty('--glow-color-b', color.b.toString());
        } else {
            smoothFollowCursor(card, targetX, targetY, 1, false);
        }
    };

    const handleCardMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
        const card = e.currentTarget;
        
        if (!card.classList.contains('card-color-shift')) {
            const rect = card.getBoundingClientRect();
            const exitX = ((e.clientX - rect.left) / rect.width) * 100;
            const exitY = ((e.clientY - rect.top) / rect.height) * 100;
            smoothFollowCursor(card, exitX, exitY, 0, false);
        }
    };

    // Form handlers
    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { id, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormStatus('loading');

        try {
            const response = await fetch('https://n8n.olvismedia.com/webhook-test/ae3af278-26e5-4344-be2e-aab45bf8c83e', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'APIKEY': '1ba7d6fa-e6ef-431c-a1e9-420fce89b3b7'
                },
                body: JSON.stringify(formData)
            });

            // Get webhook response
            const data = await response.json();
            console.log('Webhook response:', data);

            // Check if webhook processed successfully
            if (response.ok && data) {
                setFormStatus('success');
                setToastType('success');
                setToastMessage(language === 'ja' ? '送信完了' : 'Sent.');
                setShowToast(true);
                setFormData({
                    fullName: '',
                    email: '',
                    businessName: '',
                    business: ''
                });
            } else {
                throw new Error('Webhook did not confirm success');
            }
        } catch (error) {
            setFormStatus('error');
            setToastType('error');
            setToastMessage(language === 'ja' ? 'エラー。再試行してください。' : 'Failed. Try again.');
            setShowToast(true);
            console.error('Form submission error:', error);
        }
    };

    // Auto-hide toast after 5 seconds
    useEffect(() => {
        if (showToast) {
            const timer = setTimeout(() => {
                setShowToast(false);
                setFormStatus('idle');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [showToast]);

    useEffect(() => {
        const handleScroll = () => {
          const scrollHeight = document.documentElement.scrollHeight;
          const scrollTop = document.documentElement.scrollTop;
          const clientHeight = document.documentElement.clientHeight;
          
          const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;
          setShowVignette(!isAtBottom);
        };
      
        window.addEventListener('scroll', handleScroll);
        
        const currentAnimationData = animationData.current;
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
            currentAnimationData.forEach(data => {
                if (data.animationFrame) {
                    cancelAnimationFrame(data.animationFrame);
                }
            });
            currentAnimationData.clear();
        };
    }, []);

    // Mobile scroll-based glow effect
    useEffect(() => {
        const isMobile = () => window.innerWidth <= 768;
        
        const updateCardGlows = () => {
            if (!isMobile()) return;
            
            const viewportCenter = window.innerHeight / 2;
            const resultsCards = document.querySelectorAll('.results-card, .demo-card');
            
            resultsCards.forEach((card) => {
                const htmlCard = card as HTMLElement;
                const rect = htmlCard.getBoundingClientRect();
                const cardCenter = rect.top + rect.height / 2;
                const distanceFromCenter = Math.abs(cardCenter - viewportCenter);
                
                // Within 150px of center gets glow effect
                if (distanceFromCenter < 150 && rect.top > -rect.height && rect.bottom < window.innerHeight + rect.height) {
                    // Calculate intensity based on distance (closer = brighter)
                    const intensity = Math.max(0, (150 - distanceFromCenter) / 150);
                    htmlCard.classList.add('mobile-scroll-glow');
                    htmlCard.style.setProperty('--glow-intensity', intensity.toString());
                } else {
                    htmlCard.classList.remove('mobile-scroll-glow');
                }
            });
        };

        const handleMobileScroll = () => {
            if (isMobile()) {
                requestAnimationFrame(updateCardGlows);
            }
        };

        window.addEventListener('scroll', handleMobileScroll, { passive: true });
        window.addEventListener('resize', handleMobileScroll);
        
        // Initial check
        updateCardGlows();
        
        return () => {
            window.removeEventListener('scroll', handleMobileScroll);
            window.removeEventListener('resize', handleMobileScroll);
        };
    }, []);

      return (
        <div 
          className="min-h-screen text-white relative overflow-hidden"
          style={{
            background: '#000000',
          }}
        >
        
        {/* Red Crimzon Circle with Orange Bottom Glow - Scrolls with Page */}
        <div className="absolute inset-0 pointer-events-none z-0" style={{ minHeight: '400vh' }}>
          {/* Pure Black Background */}
          <div 
            className="absolute inset-0"
            style={{
              background: '#000000',
            }}
          ></div>

          {/* Large Grid Pattern - Full Page */}
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `
                linear-gradient(rgba(255, 69, 0, 0.08) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255, 69, 0, 0.08) 1px, transparent 1px)
              `,
              backgroundSize: '120px 120px',
            }}
          ></div>

          {/* Diagonal Lines - Corner to Opposite Corner in Each Square */}
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `
                linear-gradient(45deg, transparent 49.583%, rgba(255, 100, 0, 0.12) 49.583%, rgba(255, 100, 0, 0.12) 50.417%, transparent 50.417%),
                linear-gradient(-45deg, transparent 49.583%, rgba(255, 100, 0, 0.12) 49.583%, rgba(255, 100, 0, 0.12) 50.417%, transparent 50.417%)
              `,
              backgroundSize: '120px 120px',
              backgroundPosition: '0px 0px',
            }}
          ></div>

          {/* White overlay on triangles with varying opacity for 3D effect */}
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `
                conic-gradient(from 0deg at 50% 50%, 
                  rgba(255, 255, 255, 0.09) 0deg, 
                  rgba(255, 255, 255, 0.09) 45deg, 
                  rgba(255, 255, 255, 0.03) 45deg, 
                  rgba(255, 255, 255, 0.03) 135deg, 
                  rgba(255, 255, 255, 0.01) 135deg, 
                  rgba(255, 255, 255, 0.01) 225deg, 
                  rgba(255, 255, 255, 0.06) 225deg, 
                  rgba(255, 255, 255, 0.06) 315deg,
                  rgba(255, 255, 255, 0.09) 315deg,
                  rgba(255, 255, 255, 0.09) 360deg)
              `,
              backgroundSize: '120px 120px',
              backgroundPosition: '0px 0px',
            }}
          ></div>

          {/* Main RED Circle - Like Crimzon - At Top */}
          <div 
            className="absolute"
            style={{
              top: '-10%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '1600px',
              height: '1600px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(220, 38, 38, 0.6) 0%, rgba(185, 28, 28, 0.4) 30%, rgba(139, 0, 0, 0.25) 50%, transparent 70%)',
              filter: 'blur(120px)',
            }}
          ></div>

          {/* Inner Bright Red Core */}
          <div 
            className="absolute"
            style={{
              top: '5%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '700px',
              height: '700px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255, 69, 0, 0.5) 0%, rgba(220, 20, 60, 0.35) 40%, transparent 70%)',
              filter: 'blur(80px)',
            }}
          ></div>

          {/* ORANGE Glow at Bottom Edge of Circle */}
          <div 
            className="absolute left-0 right-0"
            style={{
              top: '35%',
              height: '350px',
              background: 'radial-gradient(ellipse 120% 100% at 50% 0%, rgba(255, 140, 0, 0.6) 0%, rgba(255, 100, 0, 0.45) 30%, rgba(255, 69, 0, 0.3) 50%, transparent 75%)',
              filter: 'blur(90px)',
            }}
          ></div>

          {/* Additional Red Glow - Middle Left */}
          <div 
            className="absolute"
            style={{
              top: '1500px',
              left: '-10%',
              width: '900px',
              height: '900px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(220, 38, 38, 0.6) 0%, rgba(139, 0, 0, 0.4) 40%, transparent 70%)',
              filter: 'blur(100px)',
            }}
          ></div>

          {/* Additional Orange Glow - Middle Right */}
          <div 
            className="absolute"
            style={{
              top: '2200px',
              right: '-10%',
              width: '800px',
              height: '800px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255, 140, 0, 0.6) 0%, rgba(255, 100, 0, 0.4) 40%, transparent 70%)',
              filter: 'blur(110px)',
            }}
          ></div>

          {/* Additional Crimson Glow - Bottom Center */}
          <div 
            className="absolute"
            style={{
              top: '3000px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '1000px',
              height: '1000px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(220, 20, 60, 0.6) 0%, rgba(185, 28, 28, 0.4) 40%, transparent 70%)',
              filter: 'blur(120px)',
            }}
          ></div>

          {/* Additional Orange-Red Glow - Far Bottom Left */}
          <div 
            className="absolute"
            style={{
              top: '3800px',
              left: '10%',
              width: '700px',
              height: '700px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255, 69, 0, 0.6) 0%, rgba(220, 38, 38, 0.4) 40%, transparent 70%)',
              filter: 'blur(90px)',
            }}
          ></div>
        </div>
        
        {/* Toast Notification */}
        <div 
          className={`fixed top-8 right-8 z-[9999] transition-all duration-500 transform ${
            showToast ? 'translate-x-0 opacity-100' : 'translate-x-[500px] opacity-0'
          }`}
        >
          <div className={`flex items-center gap-4 px-6 py-4 rounded-2xl shadow-2xl backdrop-blur-xl border-2 min-w-[320px] ${
            toastType === 'success' 
              ? 'bg-green-500/20 border-green-500/50' 
              : 'bg-red-500/20 border-red-500/50'
          }`}>
            {/* Icon */}
            <div className="flex-shrink-0">
              {toastType === 'success' ? (
                <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            
            {/* Message */}
            <p className={`font-medium ${
              toastType === 'success' ? 'text-green-100' : 'text-red-100'
            }`}>
              {toastMessage}
            </p>
            
            {/* Close Button */}
            <button 
              onClick={() => setShowToast(false)}
              className="flex-shrink-0 ml-auto hover:opacity-70 transition-opacity"
            >
              <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        
        {/* hero */}
        <div className="h-screen flex items-center justify-center px-8 relative z-10">
          <div className="text-center relative max-w-5xl">
            {/* glass bg */}
            <div className="absolute inset-0 hero-glass-bg rounded-3xl transform scale-110"></div>
            
            <h1 className="text-3xl md:text-6xl font-medium leading-tight tracking-tight relative z-10 mb-8 text-white">
                <span className="block">{t.hero.title1}</span>
                <span className="block">
                  {language === 'ja' ? (
                    <span className="relative inline-block">
                      {t.hero.title2}
                      <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-white to-transparent opacity-30"></div>
                    </span>
                  ) : (
                    <>
                      We Sell{' '}
                      <span className="relative inline-block" style={{ color: '#ffffff', WebkitTextFillColor: '#ffffff' }}>
                        <span className="relative z-10">Output.</span>
                        <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-white to-transparent opacity-30"></div>
                      </span>
                    </>
                  )}
                </span>
            </h1>
            
            {/* Subtext */}
            <p className="text-sm md:text-xl text-gray-300 font-light leading-relaxed relative z-10 max-w-3xl mx-auto mb-8">
                {t.hero.subtitle}
            </p>
            
            {/* CTA Button */}
            <button 
                className="testimonialglasscontainer text-gray-100 font-regular py-4 px-8 rounded-xl transition-all duration-300 hover:opacity-50 relative z-10"
                style={{
                    mask: 'radial-gradient(ellipse 80% 60% at center, black 40%, transparent 100%)',
                    WebkitMask: 'radial-gradient(ellipse 80% 60% at center, black 40%, transparent 100%)'
                } as React.CSSProperties}
                onClick={() => {
                    document.querySelector('form')?.scrollIntoView({ behavior: 'smooth' });
                }}
            >
                {t.hero.cta1}
            </button>

            {/* Second CTA Button - Brutal Truth */}
            <button 
                className="startnowbutton secondary"
                style={{
                    maskImage: 'radial-gradient(ellipse 80% 60% at center, black 40%, transparent 100%)',
                    WebkitMask: 'radial-gradient(ellipse 80% 60% at center, black 40%, transparent 100%)'
                } as React.CSSProperties}
                onClick={() => {
                    document.getElementById('brutal-truth')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
            >
                {language === 'ja' ? '厳しい真実を読む' : 'Read the brutal truth'}
            </button>
          </div>
        </div>

        {/* REPLACE TASKS SECTION - NEW */}
        <div className="w-full px-4 md:px-8 py-16 md:py-20 relative z-10">
          <div className="max-w-7xl mx-auto">
            
            {/* Section Header */}
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
                {t.replaceTasks.title}
              </h2>
              <p className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto">
                {t.replaceTasks.subtitle}
              </p>
            </div>

            {/* Tasks Grid - Problem → Solution Cards - Color Shifting Version */}
            <div className="space-y-6 md:space-y-8">
              {t.replaceTasks.tasks.map((task, index) => (
                <div 
                  key={index}
                  className="card-color-shift group relative bg-black rounded-2xl overflow-hidden transition-all duration-500"
                  onMouseEnter={handleCardMouseEnter}
                  onMouseMove={handleCardMouseMove}
                  onMouseLeave={handleCardMouseLeave}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                    
                    {/* PROBLEM SIDE - Red accent */}
                    <div className="relative p-6 md:p-8 bg-black border-r border-gray-800/50">
                      {/* Problem Label */}
                      <div className="inline-flex items-center gap-2 mb-4">
                        <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-red-400 text-xs font-semibold uppercase tracking-wider">Current State</span>
                      </div>

                      {/* Time Wasted Badge */}
                      <div className="mb-4">
                        <div className="inline-flex items-center gap-2 border border-red-500/30 rounded-lg px-4 py-2">
                          <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-red-300 text-sm font-semibold">{task.timeWasted}</span>
                        </div>
                      </div>

                      {/* Problem Title */}
                      <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                        {task.problem}
                      </h3>

                      {/* Problem Description */}
                      <p className="text-gray-400 text-base leading-relaxed">
                        {task.problemDetail}
                      </p>
                    </div>

                    {/* SOLUTION SIDE - Green accent */}
                    <div className="relative p-6 md:p-8 bg-black">
                      {/* Solution Label */}
                      <div className="inline-flex items-center gap-2 mb-4">
                        <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-green-400 text-xs font-semibold uppercase tracking-wider">Automated</span>
                      </div>

                      {/* Time Saved Badge */}
                      <div className="mb-4">
                        <div className="inline-flex items-center gap-2 border border-green-500/30 rounded-lg px-4 py-2">
                          <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-green-300 text-sm font-semibold">{task.timeSaved}</span>
                        </div>
                      </div>

                      {/* Solution Title */}
                      <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                        {task.solution}
                      </h3>

                      {/* Solution Description */}
                      <p className="text-gray-400 text-base leading-relaxed mb-4">
                        {task.solutionDetail}
                      </p>

                      {/* Benefits List */}
                      <div className="flex flex-wrap gap-2 mt-4">
                        {task.benefits.map((benefit, i) => (
                          <span 
                            key={i}
                            className="inline-flex items-center gap-1.5 border border-green-500/30 rounded-lg px-3 py-1.5 text-xs text-green-300 font-medium"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {benefit}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Center Arrow - Visible on Desktop */}
                    <div className="hidden lg:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                      <svg className="w-10 h-10 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </div>

                  </div>
                </div>
              ))}
            </div>

            {/* Bottom CTA */}
            <div className="text-center mt-12">
              <button 
                className="startnowbutton huge"
                onClick={() => {
                  document.querySelector('form')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                {language === 'ja' ? '今すぐ自動化を開始' : 'Automate These Tasks Now'}
              </button>
              <p className="text-gray-400 mt-4 text-sm">
                {language === 'ja' ? '無料相談で始めましょう' : 'Free consultation • No credit card required'}
              </p>
            </div>

          </div>
        </div>

        {/* Content container */}
        <div className="flex flex-col items-center justify-center px-8 py-6 relative z-10">

          {/* AI Automation Section - Benefits Results - 2 Big + 3 Small */}
          <div className="w-full px-2 md:px-8" style={{ marginTop: '4rem', marginBottom: '4rem' }}>

            {/* Section Intro Text */}
            <div className="text-center mb-12 max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
                {language === 'ja' ? 'AI自動化は単なる経費ではありません' : 'AI Automation Isn\'t Just Another Expense'}
              </h2>
              <p className="text-lg md:text-xl text-gray-400 leading-relaxed">
                {language === 'ja' 
                  ? '従業員を雇うのではなく、時間を節約し、収益を増やし、24時間365日稼働するシステムに投資してください。'
                  : 'It\'s an investment that saves time, increases revenue, and works around the clock—without hiring anyone.'
                }
              </p>
            </div>

            {/* Big Cards Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
              
              {/* Big Card 1: Time Savings - Purple */}
              <div className="bg-gray-900 rounded-3xl p-6 md:p-10 border-2 border-purple-500/30 hover:border-purple-500 transition-all duration-300 card-with-light card-with-light-purple results-card" onMouseEnter={handleCardMouseEnter} onMouseMove={handleCardMouseMove} onMouseLeave={handleCardMouseLeave}>
                <div className="flex justify-center mb-4">
                  <ClockIcon className="w-12 h-12 md:w-16 md:h-16 text-white transition-colors duration-300 hover-icon-purple" />
                </div>
                <div className="text-center">
                  <div className="text-5xl md:text-7xl font-bold text-white mb-3">15h</div>
                  <div className="text-purple-400 text-xl md:text-2xl font-semibold mb-2">
                    {language === 'ja' ? '週間時短' : 'Saved Per Week'}
                  </div>
                  <div className="text-purple-300 text-sm font-medium mb-4 opacity-80">
                    {language === 'ja' ? '自動化による時間節約' : 'Through Automation'}
                  </div>
                  <p className="text-gray-300 text-base md:text-lg leading-relaxed">
                    {language === 'ja' 
                      ? '反復タスクを排除し、チームが戦略的な仕事に集中できるようにします。年間780時間の節約に相当します。'
                      : 'Eliminate repetitive tasks so your team focuses on strategic work. Equivalent to 780 hours saved annually.'
                    }
                  </p>
                </div>
              </div>

              {/* Big Card 2: Revenue Impact - Green */}
              <div className="bg-gray-900 rounded-3xl p-6 md:p-10 border-2 border-green-500/30 hover:border-green-500 transition-all duration-300 card-with-light card-with-light-green results-card" onMouseEnter={handleCardMouseEnter} onMouseMove={handleCardMouseMove} onMouseLeave={handleCardMouseLeave}>
                <div className="flex justify-center mb-4">
                  <ChartBarIcon className="w-12 h-12 md:w-16 md:h-16 text-white transition-colors duration-300 hover-icon-green" />
                </div>
                <div className="text-center">
                  <div className="text-5xl md:text-7xl font-bold text-white mb-3">3.2x</div>
                  <div className="text-green-400 text-xl md:text-2xl font-semibold mb-2">
                    {language === 'ja' ? 'ROI平均' : 'Average ROI'}
                  </div>
                  <div className="text-green-300 text-sm font-medium mb-4 opacity-80">
                    {language === 'ja' ? '6ヶ月以内' : 'Within 6 Months'}
                  </div>
                  <p className="text-gray-300 text-base md:text-lg leading-relaxed">
                    {language === 'ja'
                      ? 'AI自動化への投資1円につき、クライアントは平均3.2円の利益を得ています。運用コストを60%削減しながら収益を増加させます。'
                      : 'For every dollar invested in AI automation, clients gain $3.20 back. Increase revenue while reducing operating costs by 60%.'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Small Cards Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
              
              {/* Small Card 1: Response Time - Blue */}
              <div className="bg-gray-900 rounded-2xl p-5 md:p-6 border-2 border-blue-500/30 hover:border-blue-500 transition-all duration-300 card-with-light card-with-light-blue results-card" onMouseEnter={handleCardMouseEnter} onMouseMove={handleCardMouseMove} onMouseLeave={handleCardMouseLeave}>
                <div className="flex justify-center mb-3">
                  <BoltIcon className="w-10 h-10 text-white transition-colors duration-300 hover-icon-blue" />
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-white mb-1">&lt;1min</div>
                  <div className="text-blue-400 text-base md:text-lg font-semibold mb-2">
                    {language === 'ja' ? '応答時間' : 'Response Time'}
                  </div>
                  <p className="text-gray-400 text-sm">
                    {language === 'ja'
                      ? 'リード、顧客、問い合わせに即座に対応。機会を逃しません。'
                      : 'Instant response to leads, customers, and inquiries. Never miss an opportunity.'
                    }
                  </p>
                </div>
              </div>

              {/* Small Card 2: Automation Rate - Orange */}
              <div className="bg-gray-900 rounded-2xl p-5 md:p-6 border-2 border-orange-500/30 hover:border-orange-500 transition-all duration-300 card-with-light card-with-light-orange results-card" onMouseEnter={handleCardMouseEnter} onMouseMove={handleCardMouseMove} onMouseLeave={handleCardMouseLeave}>
                <div className="flex justify-center mb-3">
                  <CpuChipIcon className="w-10 h-10 text-white transition-colors duration-300 hover-icon-orange" />
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-white mb-1">85%</div>
                  <div className="text-orange-400 text-base md:text-lg font-semibold mb-2">
                    {language === 'ja' ? 'タスク自動化' : 'Tasks Automated'}
                  </div>
                  <p className="text-gray-400 text-sm">
                    {language === 'ja'
                      ? '手作業を排除し、チームが重要な仕事に集中できるようにします。'
                      : 'Eliminate manual work so your team focuses on what matters most.'
                    }
                  </p>
                </div>
              </div>

              {/* Small Card 3: 24/7 Operations - Red */}
              <div className="bg-gray-900 rounded-2xl p-5 md:p-6 border-2 border-red-500/30 hover:border-red-500 transition-all duration-300 card-with-light card-with-light-red results-card" onMouseEnter={handleCardMouseEnter} onMouseMove={handleCardMouseMove} onMouseLeave={handleCardMouseLeave}>
                <div className="flex justify-center mb-3">
                  <ArrowPathIcon className="w-10 h-10 text-white transition-colors duration-300 hover-icon-red" />
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-white mb-1">24/7</div>
                  <div className="text-red-400 text-base md:text-lg font-semibold mb-2">
                    {language === 'ja' ? '継続運用' : 'Always Running'}
                  </div>
                  <p className="text-gray-400 text-sm">
                    {language === 'ja'
                      ? '休憩なしで動作し続け、タイムゾーンに関係なく結果を提供します。'
                      : 'Works non-stop, delivering results regardless of time zones or holidays.'
                    }
                  </p>
                </div>
              </div>
            </div>

          </div>

          {/* AI INFRASTRUCTURE SHOWCASE SECTION - CAROUSEL */}
          <div className="w-full px-8" style={{ marginTop: '8rem', marginBottom: '3rem' }}>
            <div className="text-center mb-6">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
                {t.infrastructure.title}
              </h2>
              <p className="text-base md:text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed px-4">
                {t.infrastructure.description}
              </p>
            </div>

            {/* Subtle Rainbow Glow - Behind Carousel */}
            <div 
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(255, 100, 100, 0.08) 0%, rgba(255, 200, 100, 0.06) 20%, rgba(255, 255, 150, 0.05) 40%, rgba(150, 255, 150, 0.05) 60%, rgba(100, 150, 255, 0.06) 80%, rgba(200, 100, 255, 0.08) 100%)',
                filter: 'blur(80px)',
              }}
            ></div>

            {/* Carousel Container */}
            <div className="relative max-w-7xl mx-auto">
              {/* Infrastructure Cards Array */}
              <div className="overflow-visible px-4 md:px-0">
                <div 
                  className="flex transition-transform duration-500 ease-in-out gap-4 md:gap-6"
                  style={{ 
                    transform: infrastructureIndex === 0 
                      ? 'translateX(7.5%)' 
                      : infrastructureIndex === 1 
                        ? 'translateX(calc(7.5% - 85% - 1.5rem))' 
                        : 'translateX(calc(7.5% - 170% - 3rem))'
                  }}
                >
                  {/* Card 1: Customer Support Infrastructure - NOW FIRST */}
                  <div className="w-full md:w-[85%] flex-shrink-0">
                    <div 
                      className={`bg-gray-900 rounded-3xl p-4 md:p-8 card-with-light card-with-light-blue demo-card transition-all duration-500 relative ${
                        infrastructureIndex === 0 ? 'scale-100 opacity-100' : 'scale-95 opacity-60'
                      }`}
                      onMouseEnter={handleCardMouseEnter} 
                      onMouseMove={handleCardMouseMove} 
                      onMouseLeave={handleCardMouseLeave}
                    >
                      <div className="mb-6">
                        <div className="w-full bg-gray-800 rounded-2xl mb-6 relative overflow-hidden aspect-video">
                          {/* Popular Badge - Ribbon on image top */}
                          <div className="absolute top-0 right-0 z-10">
                            <div className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-3 py-1.5 rounded-lg text-xs font-medium">
                              <span>{language === 'ja' ? '人気' : 'Popular'}</span>
                            </div>
                          </div>
                          
                          <img 
                            src={imageUrls.demo3}
                            alt="Customer Support Infrastructure Demo Screenshot" 
                            className="w-full h-full object-cover"
                            loading="eager"
                            decoding="async"
                            fetchPriority="high"
                            width="1920"
                            height="1080"
                          />
                        </div>
                        
                        <h3 className="text-xl md:text-2xl font-bold text-white mb-4">
                          {language === 'ja' ? '完全なカスタマーサポートインフラ' : 'Full Customer Support Infrastructure'}
                        </h3>
                        <p className="text-gray-400 text-lg leading-relaxed mb-6">
                          {language === 'ja'
                            ? '複数のチャネルにわたる顧客の問い合わせを処理する完全なサポートシステム。チケット管理、応答自動化、エスカレーションワークフロー。'
                            : 'Complete support system that handles customer inquiries across multiple channels. Ticket management, response automation, and escalation workflows.'
                          }
                        </p>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex items-center text-blue-400">
                          <div className="w-2 h-2 bg-blue-400 rounded-full mr-3"></div>
                          <span className="text-sm">{language === 'ja' ? 'マルチチャンネルサポート' : 'Multi-channel support'}</span>
                        </div>
                        <div className="flex items-center text-blue-400">
                          <div className="w-2 h-2 bg-blue-400 rounded-full mr-3"></div>
                          <span className="text-sm">{language === 'ja' ? '自動チケット管理' : 'Automated ticket management'}</span>
                        </div>
                        <div className="flex items-center text-blue-400">
                          <div className="w-2 h-2 bg-blue-400 rounded-full mr-3"></div>
                          <span className="text-sm">{language === 'ja' ? 'スマートエスカレーション' : 'Smart escalation workflows'}</span>
                        </div>
                        <div className="flex items-center text-blue-400">
                          <div className="w-2 h-2 bg-blue-400 rounded-full mr-3"></div>
                          <span className="text-sm">{language === 'ja' ? '24時間365日の応答' : '24/7 instant responses'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Appointment Setter */}
                  <div className="w-full md:w-[85%] flex-shrink-0">
                    <div 
                      className={`bg-gray-900 rounded-3xl p-4 md:p-8 card-with-light card-with-light-green demo-card transition-all duration-500 ${
                        infrastructureIndex === 1 ? 'scale-100 opacity-100' : 'scale-95 opacity-60'
                      }`}
                      onMouseEnter={handleCardMouseEnter} 
                      onMouseMove={handleCardMouseMove} 
                      onMouseLeave={handleCardMouseLeave}
                    >
                      <div className="mb-6">
                        <div className="w-full bg-gray-800 rounded-2xl mb-6 relative overflow-hidden aspect-video">
                          <img 
                            src={imageUrls.demo2}
                            alt="Telegram WhatsApp System Demo Screenshot" 
                            className="w-full h-full object-cover"
                            loading="eager"
                            decoding="async"
                            fetchPriority="high"
                            width="1920"
                            height="1080"
                          />
                        </div>
                        
                        <h3 className="text-xl md:text-2xl font-bold text-white mb-4">
                          {t.demos.appointment.title}
                        </h3>
                        <p className="text-gray-400 text-lg leading-relaxed mb-6">
                          {t.demos.appointment.description}
                        </p>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex items-center text-green-400">
                          <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                          <span className="text-sm">{t.demos.appointment.features.calendar}</span>
                        </div>
                        <div className="flex items-center text-green-400">
                          <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                          <span className="text-sm">{t.demos.appointment.features.email}</span>
                        </div>
                        <div className="flex items-center text-green-400">
                          <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                          <span className="text-sm">{t.demos.appointment.features.multiplatform}</span>
                        </div>
                        <div className="flex items-center text-green-400">
                          <div className="w-2 h-2 bg-green-400 rounded-full mr-3"></div>
                          <span className="text-sm">{t.demos.appointment.features.availability}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 3: Lead Scraper */}
                  <div className="w-full md:w-[85%] flex-shrink-0">
                    <div 
                      className={`bg-gray-900 rounded-3xl p-4 md:p-8 card-with-light card-with-light-orange demo-card transition-all duration-500 ${
                        infrastructureIndex === 2 ? 'scale-100 opacity-100' : 'scale-95 opacity-60'
                      }`}
                      onMouseEnter={handleCardMouseEnter} 
                      onMouseMove={handleCardMouseMove} 
                      onMouseLeave={handleCardMouseLeave}
                    >
                      <div className="mb-6">
                        <div className="w-full bg-gray-800 rounded-2xl mb-6 relative overflow-hidden aspect-video">
                          <img 
                            src={imageUrls.demo1}
                            alt="Lead Scraper Demo Screenshot" 
                            className="w-full h-full object-cover"
                            loading="eager"
                            decoding="async"
                            fetchPriority="high"
                            width="1920"
                            height="1080"
                          />
                        </div>
                        
                        <h3 className="text-xl md:text-2xl font-bold text-white mb-4">
                          {t.demos.leadScraper.title}
                        </h3>
                        <p className="text-gray-400 text-lg leading-relaxed mb-6">
                          {t.demos.leadScraper.description}
                        </p>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex items-center text-orange-400">
                          <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                          <span className="text-sm">{t.demos.leadScraper.features.extraction}</span>
                        </div>
                        <div className="flex items-center text-orange-400">
                          <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                          <span className="text-sm">{t.demos.leadScraper.features.analysis}</span>
                        </div>
                        <div className="flex items-center text-orange-400">
                          <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                          <span className="text-sm">{t.demos.leadScraper.features.filtering}</span>
                        </div>
                        <div className="flex items-center text-orange-400">
                          <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                          <span className="text-sm">{t.demos.leadScraper.features.export}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={() => setInfrastructureIndex(Math.max(0, infrastructureIndex - 1))}
                disabled={infrastructureIndex === 0}
                className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-gray-800 hover:bg-gray-700 text-white p-3 rounded-full transition-all duration-300 ${
                  infrastructureIndex === 0 ? 'opacity-30 cursor-not-allowed' : 'opacity-100 hover:scale-110'
                }`}
              >
                <ChevronLeftIcon className="w-6 h-6" />
              </button>

              <button
                onClick={() => setInfrastructureIndex(Math.min(2, infrastructureIndex + 1))}
                disabled={infrastructureIndex === 2}
                className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-gray-800 hover:bg-gray-700 text-white p-3 rounded-full transition-all duration-300 ${
                  infrastructureIndex === 2 ? 'opacity-30 cursor-not-allowed' : 'opacity-100 hover:scale-110'
                }`}
              >
                <ChevronRightIcon className="w-6 h-6" />
              </button>

              {/* Dots Indicator */}
              <div className="flex justify-center gap-2 mt-8">
                {[0, 1, 2].map((index) => (
                  <button
                    key={index}
                    onClick={() => setInfrastructureIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      infrastructureIndex === index ? 'bg-purple-500 w-8' : 'bg-gray-600 hover:bg-gray-500'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Capabilities Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-8 md:mt-12">
              <div className="bg-gray-900 rounded-2xl p-6 card-with-light card-with-light-purple text-center" onMouseEnter={handleCardMouseEnter} onMouseMove={handleCardMouseMove} onMouseLeave={handleCardMouseLeave}>
                <div className="flex justify-center mb-3">
                  <CpuChipIcon className="w-12 h-12 text-white transition-colors duration-300 hover-icon-purple" />
                </div>
                <h4 className="text-white font-semibold mb-2">{language === 'ja' ? '複数のエージェント' : 'Multiple Agents'}</h4>
                <p className="text-gray-400 text-sm">{language === 'ja' ? '1つのシステムに完全なAIチーム' : 'Complete AI team in one system'}</p>
              </div>
              
              <div className="bg-gray-900 rounded-2xl p-6 card-with-light card-with-light-blue text-center" onMouseEnter={handleCardMouseEnter} onMouseMove={handleCardMouseMove} onMouseLeave={handleCardMouseLeave}>
                <div className="flex justify-center mb-3">
                  <ClockIcon className="w-12 h-12 text-white transition-colors duration-300 hover-icon-blue" />
                </div>
                <h4 className="text-white font-semibold mb-2">{language === 'ja' ? '24時間365日利用可能' : '24/7 Available'}</h4>
                <p className="text-gray-400 text-sm">{language === 'ja' ? '場所に関係なく、ノンストップで動作' : 'Works non-stop, regardless of your location'}</p>
              </div>
              
              <div className="bg-gray-900 rounded-2xl p-6 card-with-light card-with-light-green text-center" onMouseEnter={handleCardMouseEnter} onMouseMove={handleCardMouseMove} onMouseLeave={handleCardMouseLeave}>
                <div className="flex justify-center mb-3">
                  <CurrencyDollarIcon className="w-12 h-12 text-white transition-colors duration-300 hover-icon-green" />
                </div>
                <h4 className="text-white font-semibold mb-2">{language === 'ja' ? '低コスト' : 'Low Cost'}</h4>
                <p className="text-gray-400 text-sm">{language === 'ja' ? '月額数百万円の給与は不要' : 'No salaries of tens of thousands of dollars per month'}</p>
              </div>
              
              <div className="bg-gray-900 rounded-2xl p-6 card-with-light card-with-light-orange text-center" onMouseEnter={handleCardMouseEnter} onMouseMove={handleCardMouseMove} onMouseLeave={handleCardMouseLeave}>
                <div className="flex justify-center mb-3">
                  <RocketLaunchIcon className="w-12 h-12 text-white transition-colors duration-300 hover-icon-orange" />
                </div>
                <h4 className="text-white font-semibold mb-2">{language === 'ja' ? 'スケーラブル' : 'Scalable'}</h4>
                <p className="text-gray-400 text-sm">{language === 'ja' ? 'シンプルから複雑まで、ニーズに適応' : 'From simple to complex, adaptable to your needs'}</p>
              </div>
            </div>
          </div>

          {/* TOOLS INTEGRATION / LOGOS SECTION - FULL WIDTH */}
          <div className="tools-integration-section relative z-10 mb-8 w-full">
            {/* Constrained text container */}
            <div className="max-w-6xl mx-auto text-center mb-12 px-8">
              <h2 className="text-4xl font-bold text-white mb-4">
                Integrations with tools you already use
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                We connect your campaigns with over 200 business tools to have a complete picture of performance
              </p>
            </div>
            
            {/* Full-width logo container */}
            <div className="infinite-scroll-container w-full">
              {/* Row 1 - scrolls right */}
              <div className="logo-row move-right">
                {[...Array(4)].map((_, i) => (
                  <React.Fragment key={i}>
                    {/* OpenAI Logo with invert class */}
                    <img src="/logos/openai_logo_icon_248315.webp" alt="OpenAI" loading="lazy" className="logo-invert" />
                    <img src="/logos/Google_Sheets_logo_(2014-2020).webp" alt="Google Sheets" loading="lazy" />
                    <img src="/logos/slack-logo-thumb.webp" alt="Slack" loading="lazy" />
                    <img src="/logos/stripe.webp" alt="Stripe" loading="lazy" style={{ borderRadius: '8px' }} />
                    <img src="/logos/Salesforce.com_logo.webp" alt="Salesforce" loading="lazy" />
                    <img src="/logos/hubspot.webp" alt="HubSpot" loading="lazy" />
                    <img src="/logos/Gmail_icon_(2020).webp" alt="Gmail" loading="lazy" />
                    <img src="/logos/trello.webp" alt="Trello" loading="lazy" />
                    <img src="/logos/PayPal_Logo_Icon_2014.webp" alt="PayPal" loading="lazy" />
                    <img src="/logos/discord.webp" alt="Discord" loading="lazy" />
                  </React.Fragment>
                ))}
              </div>
              
              {/* Row 2 - scrolls left */}
              <div className="logo-row move-left">
                {[...Array(4)].map((_, i) => (
                  <React.Fragment key={i}>
                    <img src="/logos/Google_Calendar_icon_(2020).webp" alt="Google Calendar" loading="lazy" />
                    <img src="/logos/pngimg.com - whatsapp_PNG21.webp" alt="WhatsApp" loading="lazy" />
                    <img src="/logos/Microsoft_Office_Teams_(2025–present).svg.webp" alt="Microsoft Teams" loading="lazy" />
                    <img src="/logos/calendly.webp" alt="Calendly" loading="lazy" />
                    <img src="/logos/Notion-logo.webp" alt="Notion" loading="lazy" />
                    <img src="/logos/asana-logo-vector.webp" alt="Asana" loading="lazy" />
                    <img src="/logos/zapier-logo-svg-vector.webp" alt="Zapier" loading="lazy" />
                    <img src="/logos/mailchimp_icon_146054.webp" alt="Mailchimp" loading="lazy" />
                    <img src="/logos/clickup.webp" alt="ClickUp" loading="lazy" />
                    <img src="/logos/Google_Drive_icon_(2020).webp" alt="Google Drive" loading="lazy" />
                  </React.Fragment>
                ))}
              </div>
              
              {/* Row 3 - scrolls right */}
              <div className="logo-row move-right">
                {[...Array(4)].map((_, i) => (
                  <React.Fragment key={i}>
                    <img src="/logos/mongodb_original_wordmark_logo_icon_146425.webp" alt="MongoDB" loading="lazy" />
                    <img src="/logos/Postgresql_elephant.webp" alt="PostgreSQL" loading="lazy" />
                    <img src="/logos/mysql-6-logo-png-transparent.webp" alt="MySQL" loading="lazy" />
                    <img src="/logos/redis.webp" alt="Redis" loading="lazy" />
                    <img src="/logos/supabase.webp" alt="Supabase" loading="lazy" />
                    <img src="/logos/Amazon-S3-Logo.webp" alt="Amazon S3" loading="lazy" />
                    <img src="/logos/dropbox-1-logo-png-transparent.webp" alt="Dropbox" loading="lazy" />
                    <img src="/logos/free-airtable-logo-icon-svg-download-png-1254387.webp" alt="Airtable" loading="lazy" />
                    <img src="/logos/SendGrid_2016_Logo.webp" alt="SendGrid" loading="lazy" />
                    <img src="/logos/apify.webp" alt="Apify" loading="lazy" />
                  </React.Fragment>
                ))}
              </div>
              
              {/* Row 4 - scrolls left */}
              <div className="logo-row move-left">
                {[...Array(4)].map((_, i) => (
                  <React.Fragment key={i}>
                    <img src="/logos/Telegram_2019_Logo.webp" alt="Telegram" loading="lazy" />
                    <img src="/logos/pngimg.com - meta_PNG5.webp" alt="Meta" loading="lazy" />
                    <img src="/logos/pipedrive.webp" alt="Pipedrive" loading="lazy" />
                    <img src="/logos/quickbooks-2.webp" alt="QuickBooks" loading="lazy" />
                    <img src="/logos/Xero_software_logo.webp" alt="Xero" loading="lazy" />
                    <img src="/logos/zoho-logo-512.webp" alt="Zoho" loading="lazy" />
                    <img src="/logos/suverymonkey.webp" alt="SurveyMonkey" loading="lazy" />
                    <img src="/logos/openweathermap.webp" alt="OpenWeatherMap" loading="lazy" />
                    <img src="/logos/WordPress-Emblem.webp" alt="WordPress" loading="lazy" />
                    <img src="/logos/Nodimate Logo BEST.webp" alt="Nodimate" loading="lazy" />
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          {/* SPACING BETWEEN SECTIONS */}
          <div className="py-5"></div>

          {/* AI INFRASTRUCTURE SECTION - NEW TIMELINE STYLE */}
          <div className="marketing-process automation-carousel" style={{ marginBottom: '2rem', marginTop: '2rem' }}>
            <div className="process-title" style={{ marginBottom: '2rem' }}>
              <h2>{t.process.title}</h2>
            </div>
            
            <div className="mobile-carousel-controls block md:hidden">
              <button 
                onClick={() => goToPrevCard()}
                className="carousel-arrow carousel-arrow-left"
                aria-label="Previous card"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              
              <div className="carousel-indicators">
                {[0, 1, 2, 3].map((index) => (
                  <button
                    key={index}
                    onClick={() => goToCard(index)}
                    className={`carousel-dot ${automationCurrentCard === index ? 'active' : ''}`}
                    aria-label={`Go to card ${index + 1}`}
                  />
                ))}
              </div>
              
              <button 
                onClick={() => goToNextCard()}
                className="carousel-arrow carousel-arrow-right"
                aria-label="Next card"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            <div className="process-line">
              <div className="horizontal-line">
                {/* Adjusted padding for 4 items to look balanced */}
                <div className="line-dots automation-dots" style={{ padding: '0 10%' }} data-current-card={automationCurrentCard}>
                  
                  {/* Step 1 */}
                  <div className="line-dot">
                    1
                    <div className="dot-text">{t.process.step1.title}</div>
                    <div className="step-detail glasscontainer card-with-light" onMouseEnter={handleCardMouseEnter} onMouseMove={handleCardMouseMove} onMouseLeave={handleCardMouseLeave}>
                      <h4>{t.process.step1.title}</h4>
                      <p>
                        {t.process.step1.description}
                      </p>
                      <div className="marketing-step-image mt-4 mb-4">
                        <div className="relative w-full h-56 rounded-lg overflow-hidden">
                          <img 
                            src={imageUrls.automation1}
                            alt="Automation Process Step 1 - Analysis & Strategy" 
                            className="w-full h-full object-cover rounded-lg"
                            loading="lazy" 
                            decoding="async"
                            width="3840"
                            height="2160"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Step 2 */}
                  <div className="line-dot">
                    2
                    <div className="dot-text">{t.process.step2.title}</div>
                    <div className="step-detail glasscontainer card-with-light" onMouseEnter={handleCardMouseEnter} onMouseMove={handleCardMouseMove} onMouseLeave={handleCardMouseLeave}>
                      <h4>{t.process.step2.title}</h4>
                      <p>
                        {t.process.step2.description}
                      </p>
                      <div className="marketing-step-image mt-4 mb-4">
                        <div className="relative w-full h-56 rounded-lg overflow-hidden">
                          <img 
                            src={imageUrls.automation2}
                            alt="Automation Process Step 2 - Planning & Onboarding" 
                            className="w-full h-full object-cover rounded-lg"
                            loading="lazy" 
                            decoding="async"
                            width="3840"
                            height="2160"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Step 3 */}
                  <div className="line-dot">
                    3
                    <div className="dot-text">{t.process.step3.title}</div>
                    <div className="step-detail glasscontainer card-with-light" onMouseEnter={handleCardMouseEnter} onMouseMove={handleCardMouseMove} onMouseLeave={handleCardMouseLeave}>
                      <h4>{t.process.step3.title}</h4>
                      <p>
                        {t.process.step3.description}
                      </p>
                      <div className="marketing-step-image mt-4 mb-4">
                        <div className="relative w-full h-56 rounded-lg overflow-hidden">
                          <img 
                            src={imageUrls.automation3}
                            alt="Automation Process Step 3 - Development & Testing" 
                            className="w-full h-full object-cover rounded-lg"
                            loading="lazy" 
                            decoding="async"
                            width="3840"
                            height="2160"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Step 4 */}
                  <div className="line-dot">
                    4
                    <div className="dot-text">{t.process.step4.title}</div>
                    <div className="step-detail glasscontainer card-with-light" onMouseEnter={handleCardMouseEnter} onMouseMove={handleCardMouseMove} onMouseLeave={handleCardMouseLeave}>
                      <h4>{t.process.step4.title}</h4>
                      <p>
                        {t.process.step4.description}
                      </p>
                      <div className="marketing-step-image mt-4 mb-4">
                        <div className="relative w-full h-56 rounded-lg overflow-hidden">
                          <img 
                            src={imageUrls.automation4}
                            alt="Automation Process Step 4 - Delivery & Maintenance" 
                            className="w-full h-full object-cover rounded-lg"
                            loading="lazy" 
                            decoding="async"
                            width="3840"
                            height="2160"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* END AI INFRASTRUCTURE SECTION */}


        {/* SPACING BEFORE BRUTAL TRUTH */}
        <div className="py-8"></div>

        {/* BRUTAL TRUTH SECTION */}
        <div id="brutal-truth" className="w-full px-4 md:px-8 mb-16 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div 
              className="bg-gradient-to-br from-red-950/40 via-black to-black border-2 border-red-500/30 rounded-3xl p-8 md:p-12 relative overflow-hidden card-with-light card-with-light-red"
              onMouseEnter={handleCardMouseEnter}
              onMouseMove={handleCardMouseMove}
              onMouseLeave={handleCardMouseLeave}
            >
              
              {/* Warning Icon */}
              <div className="absolute top-6 right-6 opacity-10">
                <svg className="w-32 h-32 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2L1 21h22L12 2zm0 3.5L19.5 19h-15L12 5.5zM11 10v4h2v-4h-2zm0 5v2h2v-2h-2z"/>
                </svg>
              </div>

              {/* Content */}
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <svg className="w-8 h-8 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <h3 className="text-2xl md:text-3xl font-bold text-white">
                    The Brutal Truth
                  </h3>
                </div>

                <p className="text-xl md:text-2xl text-gray-200 font-medium leading-relaxed mb-6">
                  Your competitors are already automating. While you're manually processing leads, answering the same questions, and drowning in admin work—they're scaling with AI.
                </p>

                <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-8">
                  Every day you wait is another day they pull ahead. Another day your team wastes 40+ hours on tasks a bot could handle. Another day potential revenue slips through the cracks because you can't respond fast enough.
                </p>

                <div className="border-l-4 border-red-500 pl-6 mb-8">
                  <p className="text-xl md:text-2xl text-white font-bold italic">
                    "We're thinking about it" is what companies said before they got left behind.
                  </p>
                </div>

                <p className="text-lg text-gray-300">
                  The question isn't whether you should automate. It's whether you want to be the company that did—or the one that wished they had.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* SPACING BEFORE CTA */}
        <div className="py-5"></div>

        {/* CTA Form */}
        <div className="flex items-center justify-center relative py-1">
            <div className="testimonialglasscontainer w-full max-w-2xl p-10 rounded-3xl relative z-10 card-with-light" onMouseEnter={handleCardMouseEnter} onMouseMove={handleCardMouseMove} onMouseLeave={handleCardMouseLeave}>
            <form onSubmit={handleFormSubmit}>
                
                {/* Header */}
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold mb-6 text-white">
                        {t.contact.title}
                    </h2>
                    <p className="text-gray-400 text-lg">
                        {t.contact.subtitle}
                    </p>
                </div>

                {/* Form Fields */}
                <div className="space-y-5">
                    
                    {/* Name */}
                    <input
                        type="text"
                        id="fullName"
                        value={formData.fullName}
                        onChange={handleFormChange}
                        className="w-full p-4 bg-gray-800/60 border border-gray-600/60 rounded-xl focus:outline-none focus:border-white/50 focus:bg-gray-800/80 text-white text-lg transition-all duration-300 placeholder-gray-300"
                        placeholder={t.contact.fields.name}
                        required
                        disabled={formStatus === 'loading'}
                    />

                    {/* Email */}
                    <input
                        type="email"
                        id="email"
                        value={formData.email}
                        onChange={handleFormChange}
                        className="w-full p-4 bg-gray-800/60 border border-gray-600/60 rounded-xl focus:outline-none focus:border-white/50 focus:bg-gray-800/80 text-white text-lg transition-all duration-300 placeholder-gray-300"
                        placeholder={t.contact.fields.email}
                        required
                        disabled={formStatus === 'loading'}
                    />

                    {/* Business Name */}
                    <input
                        type="text"
                        id="businessName"
                        value={formData.businessName}
                        onChange={handleFormChange}
                        className="w-full p-4 bg-gray-800/60 border border-gray-600/60 rounded-xl focus:outline-none focus:border-white/50 focus:bg-gray-800/80 text-white text-lg transition-all duration-300 placeholder-gray-300"
                        placeholder={t.contact.fields.businessName}
                        required
                        disabled={formStatus === 'loading'}
                    />

                    {/* Business Type */}
                    <input
                        type="text"
                        id="business"
                        value={formData.business}
                        onChange={handleFormChange}
                        className="w-full p-4 bg-gray-800/60 border border-gray-600/60 rounded-xl focus:outline-none focus:border-white/50 focus:bg-gray-800/80 text-white text-lg transition-all duration-300 placeholder-gray-300"
                        placeholder={t.contact.fields.business}
                        required
                        disabled={formStatus === 'loading'}
                    />
                </div>

                {/* Submit Button */}
                <div className="mt-8 text-center">
                    <button
                        type="submit"
                        disabled={formStatus === 'loading'}
                        className="w-full p-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-lg font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                        {formStatus === 'loading' 
                            ? (language === 'ja' ? '送信中...' : 'Sending...') 
                            : t.contact.button
                        }
                    </button>
                    
                    <p className="text-gray-400 mt-4 text-sm">
                        {t.contact.note}
                    </p>
                </div>
            </form>
            </div>
        </div>

    </div>
    <div className={`bottom-vignette ${showVignette ? '' : 'hidden'}`} />
  </div>
);
};

export default App;